import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import EMPConfirmationBanner from "../../../shared/components/EMPConfirmationBanner/EMPConfirmationBanner";
import styles from "./OnboardingPaymentProcessorOnlineDeposits.module.scss";

const I18N_BASE =
  "DealerOnboarding.content.paymentProcessorOnlineDeposits.stripeSetup";

const NO_DATA = "--";

const StripeAccountDetails = ({
  mode,
  accountId,
  completedDate,
  onEdit,
  onConnect,
  isConnecting = false,
  showEditPreferences = true,
  stripeUrl,
  brandName = "Lincoln",
  showSuccessBanner = false,
  onDismissSuccessBanner,
  showPreferencesUpdatedBanner = false,
  onDismissPreferencesUpdatedBanner,
  showDisconnectedBanner = false,
  onDismissDisconnectedBanner,
  declinedVariant = "neverConnected",
}) => {
  const { t } = useTranslation();
  const isConnected = mode === "connected";
  const isDeclined = mode === "declined";
  const isConnect = mode === "connect";
  const showAccountTable = isConnected || isDeclined || isConnect;
  const showActionLinks = isConnected || isDeclined;
  const showYesProcessorBadge = isConnected || isConnect;
  const displayAccountId =
    accountId != null && String(accountId).trim() !== ""
      ? String(accountId).trim()
      : NO_DATA;
  const displayCompletedDate =
    completedDate != null && String(completedDate).trim() !== ""
      ? String(completedDate).trim()
      : NO_DATA;
  const declinedDescriptionKey =
    declinedVariant === "disconnected"
      ? `${I18N_BASE}.accountDetails.disconnectedDescription`
      : `${I18N_BASE}.accountDetails.declinedDescription`;

  return (
    <Box
      className={styles.accountDetailsContainer}
      data-testid={
        isConnected
          ? "stripe-account-details-content"
          : isDeclined
            ? "stripe-declined-details-content"
            : "stripe-connect-details-content"
      }
    >
      {isConnected && showSuccessBanner && (
        <EMPConfirmationBanner
          variant="success"
          primaryText={t(`${I18N_BASE}.accountDetails.successBanner`)}
          closeAriaLabel={t(
            `${I18N_BASE}.accountDetails.closeSuccessBanner`,
            "Close confirmation banner"
          )}
          onClose={onDismissSuccessBanner}
          testId="stripe-account-success-banner"
        />
      )}

      {isDeclined && showDisconnectedBanner && (
        <EMPConfirmationBanner
          variant="success"
          primaryText={t(`${I18N_BASE}.accountDetails.disconnectedBanner`)}
          closeAriaLabel={t(
            `${I18N_BASE}.accountDetails.closeDisconnectedBanner`,
            "Close confirmation banner"
          )}
          onClose={onDismissDisconnectedBanner}
          testId="stripe-disconnected-banner"
        />
      )}

      {isDeclined &&
        showPreferencesUpdatedBanner &&
        !showDisconnectedBanner && (
          <EMPConfirmationBanner
            variant="success"
            primaryText={t(
              `${I18N_BASE}.accountDetails.preferencesUpdatedBanner`
            )}
            closeAriaLabel={t(
              `${I18N_BASE}.accountDetails.closePreferencesUpdatedBanner`,
              "Close confirmation banner"
            )}
            onClose={onDismissPreferencesUpdatedBanner}
            testId="stripe-preferences-updated-banner"
          />
        )}

      {isDeclined && (
        <Typography
          className={styles.connectDescription}
          data-testid="stripe-declined-description"
        >
          {t(declinedDescriptionKey, {
            currentBrand: brandName,
          })}
        </Typography>
      )}

      {isConnect && (
        <Typography
          className={styles.connectDescription}
          data-testid="stripe-connect-description"
        >
          {t(`${I18N_BASE}.accountDetails.connectDescription`)}
        </Typography>
      )}

      {isConnected && (
        <Typography component="h3" className={styles.accountDetailsTitle}>
          {t(`${I18N_BASE}.accountDetails.title`)}
        </Typography>
      )}

      {showAccountTable && (
        <Box className={styles.accountDetailsCard}>
          <Box className={styles.accountDetailItem}>
            <Typography className={styles.accountDetailLabel}>
              {t(`${I18N_BASE}.accountDetails.paymentProcessorLabel`)}
            </Typography>
            <span
              className={
                showYesProcessorBadge
                  ? styles.accountDetailBadge
                  : styles.accountDetailBadgeNo
              }
              data-testid={
                isDeclined
                  ? "stripe-declined-details-processor-value"
                  : "stripe-account-details-processor-value"
              }
            >
              {showYesProcessorBadge
                ? t(`${I18N_BASE}.yes`)
                : t(`${I18N_BASE}.no`)}
            </span>
          </Box>

          <Box className={styles.accountDetailItem}>
            <Typography className={styles.accountDetailLabel}>
              {t(`${I18N_BASE}.accountDetails.accountIdLabel`)}
            </Typography>
            <Typography
              className={styles.accountDetailValue}
              data-testid="stripe-account-details-id-value"
            >
              {displayAccountId}
            </Typography>
          </Box>

          <Box className={styles.accountDetailItem}>
            <Typography className={styles.accountDetailLabel}>
              {t(`${I18N_BASE}.accountDetails.stepCompletedDateLabel`)}
            </Typography>
            <Typography
              className={styles.accountDetailValue}
              data-testid="stripe-account-details-date-value"
            >
              {displayCompletedDate}
            </Typography>
          </Box>
        </Box>
      )}

      {showActionLinks ? (
        <Box className={styles.accountDetailLinks}>
          {showEditPreferences && (
            <EMPButton
              variant="text"
              disableRipple
              onClick={onEdit}
              leftIcon={
                <EditOutlinedIcon className={styles.accountDetailLinkIcon} />
              }
              className={styles.accountDetailLink}
              testId="stripe-account-details-edit-preferences"
            >
              {t(`${I18N_BASE}.accountDetails.editPreferences`)}
            </EMPButton>
          )}

          <EMPButton
            variant="text"
            disableRipple
            href={stripeUrl}
            target="_blank"
            rel="noopener noreferrer"
            leftIcon={
              <OpenInNewIcon className={styles.accountDetailLinkIcon} />
            }
            className={styles.accountDetailLink}
            testId="stripe-account-details-stripe-account"
          >
            {t(`${I18N_BASE}.accountDetails.stripeAccount`)}
          </EMPButton>
        </Box>
      ) : (
        <Box className={styles.saveButtonContainer}>
          <EMPButton
            variant="contained"
            onClick={onConnect}
            disabled={isConnecting || !onConnect}
            testId="stripe-connect-account-button"
            className={styles.saveButton}
          >
            {t(`${I18N_BASE}.accountDetails.connectStripeAccount`)}
          </EMPButton>
        </Box>
      )}
    </Box>
  );
};

StripeAccountDetails.propTypes = {
  mode: PropTypes.oneOf(["connected", "connect", "declined"]).isRequired,
  accountId: PropTypes.string,
  completedDate: PropTypes.string,
  onEdit: PropTypes.func,
  onConnect: PropTypes.func,
  isConnecting: PropTypes.bool,
  showEditPreferences: PropTypes.bool,
  stripeUrl: PropTypes.string.isRequired,
  brandName: PropTypes.string,
  showSuccessBanner: PropTypes.bool,
  onDismissSuccessBanner: PropTypes.func,
  showPreferencesUpdatedBanner: PropTypes.bool,
  onDismissPreferencesUpdatedBanner: PropTypes.func,
  showDisconnectedBanner: PropTypes.bool,
  onDismissDisconnectedBanner: PropTypes.func,
  declinedVariant: PropTypes.oneOf(["neverConnected", "disconnected"]),
};

StripeAccountDetails.defaultProps = {
  accountId: "",
  completedDate: "",
  onEdit: undefined,
  onConnect: undefined,
  isConnecting: false,
  showEditPreferences: true,
  brandName: "Lincoln",
  showSuccessBanner: false,
  onDismissSuccessBanner: undefined,
  showPreferencesUpdatedBanner: false,
  onDismissPreferencesUpdatedBanner: undefined,
  showDisconnectedBanner: false,
  onDismissDisconnectedBanner: undefined,
  declinedVariant: "neverConnected",
};

export default StripeAccountDetails;
